import React, { memo, useState, useCallback } from 'react';
import type { Question } from '../../types';
import { QuestionAnimationPlayer } from './QuestionAnimationPlayer';
import { QuestionImageOrVideo } from './QuestionImageOrVideo';

interface FillinQuestionProps {
  question: Question;
  questionNumber?: number;
  selectedAnswer?: string;
  isAnswered?: boolean;
  onAnswer?: (answerId: string) => void;
  streakMessage?: string | null;
}

/**
 * FillinQuestion — Numeric fill-in format rendered as tap tiles.
 *
 * Displays the answer options as large tappable tiles (no text input).
 * Options show the value prominently. Correct/wrong states apply on answer.
 */

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E'];

export const FillinQuestion: React.FC<FillinQuestionProps> = memo(({
  question,
  questionNumber = 1,
  selectedAnswer,
  isAnswered,
  onAnswer,
  streakMessage,
}) => {
  const [lastClickTime, setLastClickTime] = useState(0);

  const hasAnimation = !!(question as { animationId?: string | null }).animationId;
  const hasImage = !!(question as { imageUrl?: string }).imageUrl;
  const questionText = question.text || question.question;
  const correctId = question.correctAnswerId || question.answerId;

  const handleTap = useCallback((optionId: string) => {
    if (isAnswered) return;
    const now = Date.now();
    if (now - lastClickTime < 200) return;
    setLastClickTime(now);
    onAnswer?.(optionId);
  }, [isAnswered, lastClickTime, onAnswer]);

  const getOptionState = (optionId: string) => {
    if (!isAnswered) {
      return selectedAnswer === optionId ? 'selected' : 'idle';
    }
    if (optionId === correctId) return 'correct';
    if (selectedAnswer === optionId) return 'wrong';
    return 'dimmed';
  };

  return (
    <div className="fillin-question">
      {/* Animation */}
      {hasAnimation && (
        <QuestionAnimationPlayer
          animationId={(question as { animationId?: string | null }).animationId}
        />
      )}

      {/* Image */}
      {hasImage && !hasAnimation && question.imageUrl && (
        <div className="fillin-image-container">
          <QuestionImageOrVideo
            url={question.imageUrl}
            alt="question visual"
            className="fillin-image"
          />
        </div>
      )}

      {/* Question text */}
      <div className="fillin-question-text">
        <span className="professional-question-number">{questionNumber})</span>{' '}
        {questionText}
      </div>

      {/* Tap option tiles */}
      <div className="fillin-options">
        {question.options.map((option, idx) => {
          const state = getOptionState(option.id);
          return (
            <button
              key={option.id}
              type="button"
              className={`fillin-option practice-option-base fillin-option--${state}`}
              onClick={() => handleTap(option.id)}
              disabled={isAnswered}
              aria-pressed={selectedAnswer === option.id}
            >
              <span className="fillin-option-letter">{OPTION_LETTERS[idx] ?? String(idx + 1)}</span>
              <span className="fillin-option-text">{option.text}</span>
              {state === 'correct' && <span className="fillin-option-icon">✓</span>}
              {state === 'wrong' && <span className="fillin-option-icon">✗</span>}
            </button>
          );
        })}
      </div>

      {streakMessage && (
        <div className="practice-streak-message">{streakMessage}</div>
      )}
    </div>
  );
});

FillinQuestion.displayName = 'FillinQuestion';
